/* eslint-disable complexity */
import {
  AaaGameInformationAvailabilityDataType,
  AaaGameInformationConceptDetailsDataType,
  AaaGameInformationConfigurationsDataType,
  AaaGameInformationDistributionInformationDataType,
  AaaGameInformationGameExpectationsDataType,
  AaaGameInformationSupplementaryDataType,
  AaaGameInformationTechnicalCapabilitiesDataType,
  AaaGameInformationUgcDataType,
  AaaGameInformationXboxNetworkDataType,
  GameInformationConceptDetailsDataType,
  GameInformationConfigurationsDataType,
  GameInformationGameExpectationsDataType,
  GameInformationPricingAndAvailabilityDataType,
  GameInformationSupplementaryDataType,
  GameInformationTechnicalCapabilitiesDataType,
  GameInformationUgcDataType,
  GameInformationXboxNetworkDataType,
  IdAzureIntroContactDataType,
  IdXboxIntroContactDataType,
  IdXboxIntroStudioDataType,
  PitchInformationAboutYourGameDataType,
  PitchInformationAboutYourselfDataType,
  PitchInformationAskForXboxDataType,
  PitchInformationPublisherDeveloperDataType,
  SubFormsDataTypes,
} from "../../models/forms";
import {
  AaaGameInformationFormId,
  AaaGameInformationSubFormsId,
} from "../../pages/AaaGameInformationPage/AaaGameInformationPageConstants";
import {
  GameInformationFormId,
  GameInformationSubFormsId,
} from "../../pages/GameInformationPage/GameInformationPageConstants";
import {
  IdAzureIntroFormId,
  IdAzureIntroSubFormsId,
} from "../../pages/IdAzureIntroPage/IdAzureIntroPageConstants";
import {
  IdXboxIntroFormId,
  IdXboxIntroSubFormsId,
} from "../../pages/IdXboxIntroPage/IdXboxIntroPageConstants";
import {
  PitchInformationFormId,
  PitchInformationSubFormsId,
} from "../../pages/PitchInformationPage/PitchInformationPageConstants";
import {
  isAaaGameInformationAvailabilityFormValid,
  isAaaGameInformationConfigurationsFormValid,
  isAaaGameInformationDetailsFormValid,
  isAaaGameInformationDistributionInformationFormValid,
  isAaaGameInformationGameExpectationsValid,
  isAaaGameInformationSupplementaryDataFormValid,
  isAaaGameInformationTechnicalCapabilitiesFormValid,
  isAaaGameInformationUgcFormValid,
  isAaaGameInformationXboxNetworkFormValid,
  isIdAzureIntroContactAndStudioFormValid,
  isIdXboxGameInformationConfigurationsFormValid,
  isIdXboxGameInformationDetailsFormValid,
  isIdXboxGameInformationGameExpectationsValid,
  isIdXboxGameInformationPricingAndAvailabilityFormValid,
  isIdXboxGameInformationSupplementaryDataFormValid,
  isIdXboxGameInformationTechnicalCapabilitiesFormValid,
  isIdXboxGameInformationUgcFormValid,
  isIdXboxGameInformationXboxNetworkFormValid,
  isIdXboxIntroContactFormValid,
  isIdXboxIntroStudioFormValid,
  isPitchAboutYourGameValid,
  isPitchAboutYourselfValid,
  isPitchAskForXboxValid,
  isPitchInformationPublisherDeveloperValid,
} from "./formValidationHelpers";

export enum FormDataValidation {
  NonValidEmpty = "Empty",
  NonValidPartiallyFilled = "PartiallyFilled",
  Valid = "Valid",
}

const dataComparer = async <T extends SubFormsDataTypes>(
  values: T,
  validatorFunction: (_values: T) => Promise<boolean>
): Promise<FormDataValidation> => {
  if (!values?.isStarted) {
    return FormDataValidation.NonValidEmpty;
  }

  const isFormValid = await validatorFunction(values);
  if (isFormValid) {
    return FormDataValidation.Valid;
  }

  return FormDataValidation.NonValidPartiallyFilled;
};

// eslint-disable-next-line complexity
export const validateFormUxOnly = async (
  data: SubFormsDataTypes,
  formId: string,
  subFormId: string
): Promise<FormDataValidation> => {
  if (!formId || !subFormId) {
    return FormDataValidation.NonValidEmpty;
  }

  if (formId === IdXboxIntroFormId) {
    switch (subFormId) {
      case IdXboxIntroSubFormsId.ContactInformation:
        return dataComparer<IdXboxIntroContactDataType>(
          data as IdXboxIntroContactDataType,
          isIdXboxIntroContactFormValid
        );
      case IdXboxIntroSubFormsId.StudioInformation:
        return dataComparer<IdXboxIntroStudioDataType>(
          data as IdXboxIntroStudioDataType,
          isIdXboxIntroStudioFormValid
        );
    }
  }

  if (formId === GameInformationFormId) {
    switch (subFormId) {
      case GameInformationSubFormsId.ConceptDetails:
        return dataComparer<GameInformationConceptDetailsDataType>(
          data as GameInformationConceptDetailsDataType,
          isIdXboxGameInformationDetailsFormValid
        );
      case GameInformationSubFormsId.GameExpectations:
        return dataComparer<GameInformationGameExpectationsDataType>(
          data as GameInformationGameExpectationsDataType,
          isIdXboxGameInformationGameExpectationsValid
        );
      case GameInformationSubFormsId.PricingAndAvailability:
        return dataComparer<GameInformationPricingAndAvailabilityDataType>(
          data as GameInformationPricingAndAvailabilityDataType,
          isIdXboxGameInformationPricingAndAvailabilityFormValid
        );
      case GameInformationSubFormsId.TechnicalCapabilities:
        return dataComparer<GameInformationTechnicalCapabilitiesDataType>(
          data as GameInformationTechnicalCapabilitiesDataType,
          isIdXboxGameInformationTechnicalCapabilitiesFormValid
        );
      case GameInformationSubFormsId.Configurations:
        return dataComparer<GameInformationConfigurationsDataType>(
          data as GameInformationConfigurationsDataType,
          isIdXboxGameInformationConfigurationsFormValid
        );
      case GameInformationSubFormsId.XboxNetwork:
        return dataComparer<GameInformationXboxNetworkDataType>(
          data as GameInformationXboxNetworkDataType,
          isIdXboxGameInformationXboxNetworkFormValid
        );
      case GameInformationSubFormsId.Ugc:
        return dataComparer<GameInformationUgcDataType>(
          data as GameInformationUgcDataType,
          isIdXboxGameInformationUgcFormValid
        );
      case GameInformationSubFormsId.SupplementaryData:
        return dataComparer<GameInformationSupplementaryDataType>(
          data as GameInformationSupplementaryDataType,
          isIdXboxGameInformationSupplementaryDataFormValid
        );
    }
  }

  if (formId === AaaGameInformationFormId) {
    switch (subFormId) {
      case AaaGameInformationSubFormsId.ConceptDetails:
        return dataComparer<AaaGameInformationConceptDetailsDataType>(
          data as AaaGameInformationConceptDetailsDataType,
          isAaaGameInformationDetailsFormValid
        );
      case AaaGameInformationSubFormsId.DistributionInformation:
        return dataComparer<AaaGameInformationDistributionInformationDataType>(
          data as AaaGameInformationDistributionInformationDataType,
          isAaaGameInformationDistributionInformationFormValid
        );
      case AaaGameInformationSubFormsId.GameExpectations:
        return dataComparer<AaaGameInformationGameExpectationsDataType>(
          data as AaaGameInformationGameExpectationsDataType,
          isAaaGameInformationGameExpectationsValid
        );
      case AaaGameInformationSubFormsId.Availability:
        return dataComparer<AaaGameInformationAvailabilityDataType>(
          data as AaaGameInformationAvailabilityDataType,
          isAaaGameInformationAvailabilityFormValid
        );
      case AaaGameInformationSubFormsId.TechnicalCapabilities:
        return dataComparer<AaaGameInformationTechnicalCapabilitiesDataType>(
          data as AaaGameInformationTechnicalCapabilitiesDataType,
          isAaaGameInformationTechnicalCapabilitiesFormValid
        );
      case AaaGameInformationSubFormsId.Configurations:
        return dataComparer<AaaGameInformationConfigurationsDataType>(
          data as AaaGameInformationConfigurationsDataType,
          isAaaGameInformationConfigurationsFormValid
        );
      case AaaGameInformationSubFormsId.XboxNetwork:
        return dataComparer<AaaGameInformationXboxNetworkDataType>(
          data as AaaGameInformationXboxNetworkDataType,
          isAaaGameInformationXboxNetworkFormValid
        );
      case AaaGameInformationSubFormsId.Ugc:
        return dataComparer<AaaGameInformationUgcDataType>(
          data as AaaGameInformationUgcDataType,
          isAaaGameInformationUgcFormValid
        );
      case AaaGameInformationSubFormsId.SupplementaryData:
        return dataComparer<AaaGameInformationSupplementaryDataType>(
          data as AaaGameInformationSupplementaryDataType,
          isAaaGameInformationSupplementaryDataFormValid
        );
    }
  }

  if (formId === IdAzureIntroFormId) {
    switch (subFormId) {
      case IdAzureIntroSubFormsId.ContactAndStudio:
        return dataComparer<IdAzureIntroContactDataType>(
          data as IdAzureIntroContactDataType,
          isIdAzureIntroContactAndStudioFormValid
        );
    }
  }

  if (formId === PitchInformationFormId) {
    switch (subFormId) {
      case PitchInformationSubFormsId.PublisherDeveloperInformation:
        return dataComparer<PitchInformationPublisherDeveloperDataType>(
          data as PitchInformationPublisherDeveloperDataType,
          isPitchInformationPublisherDeveloperValid
        );
      case PitchInformationSubFormsId.AboutYourGame:
        return dataComparer<PitchInformationAboutYourGameDataType>(
          data as PitchInformationAboutYourGameDataType,
          isPitchAboutYourGameValid
        );
      case PitchInformationSubFormsId.AskForXbox:
        return dataComparer<PitchInformationAskForXboxDataType>(
          data as PitchInformationAskForXboxDataType,
          isPitchAskForXboxValid
        );
      case PitchInformationSubFormsId.AboutYourself:
        return dataComparer<PitchInformationAboutYourselfDataType>(
          data as PitchInformationAboutYourselfDataType,
          isPitchAboutYourselfValid
        );
    }
  }

  return FormDataValidation.NonValidEmpty;
};
